import { useQuery } from "@tanstack/react-query";

import { QueryKeys, queryClient } from "@/api/core";
import { useAuth } from "@/contexts/auth/useAuth";
import { useApi } from "@/hooks/useApi";
import { type Account, accountSchema } from "@/schemas";

import { useAccountRelationship } from "../../relationships";

interface UseAccountLookupOpts {
  withRelationship?: boolean;
}

function useAccountLookup(
  acct: string | undefined,
  opts: UseAccountLookupOpts = {},
) {
  const api = useApi();
  const { currentAccount } = useAuth();
  const { withRelationship } = opts;

  const { data: account, ...result } = useQuery<Account>({
    queryKey: QueryKeys.accounts.accountByUsername(acct!),
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/accounts/lookup?acct=${acct}`);

      return accountSchema.parse(data);
    },
    enabled: !!acct,
    initialData() {
      // Fetch an account from the cache
      const queryCache = queryClient.getQueryData(QueryKeys.accounts.global) as
        | Account[]
        | undefined;
      if (queryCache) {
        const account = queryCache.find(
          (account) => account.acct.toLowerCase() === acct?.toLowerCase(),
        );

        // Force a refetch if cached account isn't complete (assumption is zero followers/following)
        if (account?.followers_count === 0 && account?.following_count === 0) {
          return undefined;
        } else {
          return account;
        }
      }
    },
  });

  const { relationship, isInitialLoading: isRelationshipInitialLoading } =
    useAccountRelationship(account?.id, { enabled: withRelationship });

  const isBlocked = relationship?.blocked_by === true;
  const isUnavailable = currentAccount?.id === account?.id ? false : isBlocked;

  return {
    ...result,
    isLoading: result.isLoading || isRelationshipInitialLoading,
    isUnavailable,
    account: account ? { ...account, relationship } : undefined,
  };
}

export { useAccountLookup };
